/**
 * Scénario de référence, calé sur la géométrie réelle du port de Bastia.
 *
 * Sert de base aux 22 cas du §19 : chacun n'a plus qu'à surcharger ce qui le
 * concerne, ce qui rend visible en une ligne ce que le cas teste réellement.
 */
/**
 * Digue fret de Bastia. Voir config/geo/zones-port-bastia.json, dont ce bloc est
 * la transcription : les valeurs sont extraites au pixel du tracé place par
 * place fourni par la maîtrise d'ouvrage le 24 août 2026.
 */
export const PORT_BASTIA_SITE = {
    // Axe de la DALLE, c'est-à-dire la médiane des deux rangées. Cap 152,97°,
    // mesuré sur le tracé — il valait 135,4° tant qu'il était lu à l'œil.
    mole_axis: {
        north: { lat: 42.704048, lon: 9.455926 },
        south: { lat: 42.70279, lon: 9.456794 },
        half_width_m: 40,
    },
    zones: [
        {
            code: 'FRET',
            type: 'staging',
            label: 'Zone fret — digue est',
            shape: 'corridor_along_axis',
            half_width_m: 40,
            confirmed: false,
        },
        {
            // Une seule zone pour la sortie de rampe ET le contrôle : les deux
            // emprises se recouvrent et ne se distinguent pas sur le terrain. Rayon
            // VALIDÉ par la maîtrise d'ouvrage le 28 août 2026 sur vue prolongée vers
            // le sud : les 25 m couvrent l'ensemble zone de contrôle + rampe ro-ro.
            code: 'CTRL-1',
            type: 'control',
            label: 'Sortie de rampe et poste de contrôle',
            shape: 'circle',
            center: { lat: 42.702561, lon: 9.456611 },
            radius_m: 25,
            control_post_code: 'POSTE-1',
            confirmed: true,
            detection: {
                entry_buffer_m: 5,
                exit_buffer_m: 15,
                min_dwell_seconds: 8,
                max_speed_mps: 1.5,
                max_accuracy_m: 15,
                min_samples: 3,
            },
        },
    ],
    // Pas 3,50 m mesuré trois fois. La « rangée centrale » d'avant n'existait
    // pas : c'est la dalle de travail, large de 18,6 m. La rangée côté digue est
    // droite (90,8° mesuré) ; la rangée côté quai est en ÉVENTAIL — profil mesuré
    // place par place sur le tracé le 16 octobre 2026.
    parking_rows: [
        {
            area_code: 'FRET-O',
            label: 'Rangée ouest, le long du quai',
            offset_m: 9.3,
            along_offset_m: -1.71,
            slot_count: 42,
            bay_angle_deg: 90,
            bay_angle_profile: [
                { slot: 1, angle_deg: 112.6 },
                { slot: 6, angle_deg: 109.2 },
                { slot: 11, angle_deg: 106.6 },
                { slot: 16, angle_deg: 104.2 },
                { slot: 21, angle_deg: 102.4 },
                { slot: 26, angle_deg: 102.0 },
                { slot: 31, angle_deg: 100.8 },
                { slot: 36, angle_deg: 98.3 },
                { slot: 42, angle_deg: 96.4 }
            ],
            bay_pitch_m: 3.46,
            bay_width_m: 3.5,
            bay_length_m: 16.2,
            slot_code_pattern: 'O{n:02}',
        },
        {
            area_code: 'FRET-E',
            label: 'Rangée centrale, côté digue',
            offset_m: -9.3,
            along_offset_m: 2.9,
            slot_count: 31,
            bay_angle_deg: 90,
            bay_width_m: 3.5,
            bay_length_m: 16.2,
            slot_code_pattern: 'E{n:02}',
        },
    ],
};
/** Début de débarquement de référence : 4 août 2026, 08:00 UTC. */
export const DEFAULT_DISCHARGE_START_MS = Date.parse('2026-08-04T08:00:00.000Z');
export const BASELINE_SCENARIO = {
    label: 'Débarquement de référence — Bastia',
    portCallReference: 'PORTCALL-20260804-001',
    shipName: 'NAVIRE-FICTIF-1',
    dischargeStartAtMs: DEFAULT_DISCHARGE_START_MS,
    // Une seconde. L'invariance ne tient pas à cette valeur mais au report du
    // reste dans `tick()` : le monde n'avance que par pas ENTIERS de `simStepS`,
    // quel que soit le `dt` demandé. Mesuré sur la graine 20260804, dt 0,25 / 0,5
    // / 1 / 2 / 20 rendent tous 2790 mesures GNSS et 212 événements.
    simStepS: 1,
    tractorCount: 3,
    trailerCount: 20,
    // Aucun manifeste par défaut : le banc doit tourner sans dépendre d'un fichier
    // du service fret, qui n'existe pas encore pour le sens Marseille → Bastia.
    manifestTrailers: null,
    // Vide par défaut : le Durable Object le remplit avec l'identifiant du run.
    // Un moteur construit directement — les tests — garde les identifiants d'avant.
    runTag: '',
    trailerReleaseInterval: { mean_s: 90, stddev_s: 20, min_s: 45 },
    couplingDuration: { mean_s: 75, stddev_s: 15, min_s: 40 },
    // 10 à 15 s d'arrêt : à 1 point toutes les 2 s, cela donne 5 à 8 mesures,
    // de quoi calculer une position stabilisée robuste (§6).
    controlDuration: { mean_s: 12.5, stddev_s: 1.5, min_s: 10, max_s: 15 },
    uncouplingDuration: { mean_s: 60, stddev_s: 12, min_s: 30 },
    returnDuration: { mean_s: 0, stddev_s: 0 },
    tractorSpeedMps: { laden: 4.2, empty: 6.5 },
    gnssIntervalS: 2,
    heartbeatIntervalS: 60,
    slotAssignment: { strategy: 'sequential', preOccupiedSlotCodes: [] },
    outOfService: [],
    unknownTrailerRatio: 0,
    clockSkewMs: {},
    gnss: {
        // Couvre d'un coup les cas §19.7 (5 s) et §19.8 (20 s) sans scénario dédié.
        delays: [
            { delay_s: 3, weight: 3 },
            { delay_s: 5, weight: 4 },
            { delay_s: 10, weight: 2 },
            { delay_s: 20, weight: 1 },
        ],
        failure_probability: 0,
        accuracy_m: { good: 1.6, degraded: 12 },
        degraded_probability: 0,
    },
    network: {
        // 50 mesures par lot = 100 s de télémétrie. Sans regroupement, le quota
        // Cloudflare Queues du plan free tiendrait onze minutes de simulation.
        batch_size: 50,
        max_batch_delay_s: 30,
        outages: [],
        duplicate_probability: 0,
        reorder_probability: 0,
        // Trois tentatives : de quoi traverser une minute de propagation KV après
        // l'enrôlement, sans qu'un refus permanent (client révoqué, schéma
        // invalide) ne tourne en boucle jusqu'à la fin du run.
        max_send_attempts: 3,
    },
    qr: {
        read_probability: 0.92,
        misread_probability: 0,
        duplicate_read_probability: 0,
        late_read_probability: 0,
        reader_confidence: { good: 0.97, poor: 0.55 },
        advance_read_probability: 0,
        // 45 à 180 s d'avance : assez pour tomber dans la fenêtre de 120 s précédant
        // l'attelage SUIVANT tout en restant dans le cycle courant.
        advance_read_lead: { mean_s: 110, stddev_s: 35, min_s: 45, max_s: 180 },
    },
    externalApp: {
        identification_probability: 0.95,
        conflict_probability: 0,
        timestamp_offset: { mean_s: 2, stddev_s: 1 },
        transmission_delay: { mean_s: 3, stddev_s: 2, min_s: 0 },
        emit_control_post: true,
        // Référence à horloge juste, saisie propre, sans doublon ni rang : c'est
        // l'hypothèse OPTIMISTE. Elle n'est pas réaliste, elle est le point de
        // comparaison — chaque scénario dégrade ce qu'il veut mesurer.
        clock_skew_ms: 0,
        separator_noise_probability: 0,
        duplicate_probability: 0,
        emit_exit_rank: false,
    },
};
/** Copie profonde du scénario de référence, surchargée par `overrides`. */
export function scenarioWith(overrides) {
    return {
        ...structuredClone(BASELINE_SCENARIO),
        ...structuredClone(overrides),
    };
}
/** Un bloc se fusionne clé à clé ; un tableau, un scalaire ou `null` se remplace. */
function estBloc(valeur) {
    return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}
/**
 * Fusionne des surcharges dans le scénario de référence, PROFONDÉMENT.
 *
 * Une fusion de surface écrasait les sous-objets en bloc : envoyer
 * `{ qr: { misread_probability: 0.2 } }` mettait `read_probability` à
 * `undefined`, et le run partait sans QR sans qu'aucun message ne le dise. Tout
 * réglage partiel depuis l'écran était donc un piège.
 *
 * Les blocs concernés sont DÉDUITS du scénario de référence, jamais énumérés.
 * Une liste écrite à la main en avait laissé sept dehors — dont
 * `controlDuration`, dont l'écran ne surcharge que `mean_s` : `stddev_s`,
 * `min_s` et `max_s` disparaissaient en silence. Et cette même liste vivait en
 * double, dans le Durable Object et dans un test qui la réimplémentait : le test
 * validait sa propre copie, pas le code qui tourne. Une seule fonction, ici,
 * dans le paquet qui définit `EngineScenario`.
 */
export function fusionnerScenario(surcharges) {
    const base = structuredClone(BASELINE_SCENARIO);
    if (surcharges === undefined)
        return base;
    // Ce qui arrive de l'écran est du JSON : typé au mieux, jamais garanti.
    const brut = surcharges;
    const fusionne = { ...base, ...brut };
    for (const [cle, valeurBase] of Object.entries(base)) {
        if (!estBloc(valeurBase) || !(cle in brut))
            continue;
        const surcharge = brut[cle];
        // Un sous-objet remplacé par autre chose qu'un objet — null, un tableau —
        // rendrait le scénario inexploitable en silence. On garde alors la base.
        fusionne[cle] = estBloc(surcharge) ? { ...valeurBase, ...surcharge } : valeurBase;
    }
    return fusionne;
}
function estObjet(valeur) {
    return typeof valeur === 'object' && valeur !== null && !Array.isArray(valeur);
}
/**
 * Valide le catalogue de cas métier.
 *
 * Il LÈVE plutôt que de filtrer en silence : un cas mal orthographié qui
 * disparaît de la liste sans un mot laisserait croire qu'il n'existe pas, et
 * c'est précisément le genre de silence que ce banc doit dénoncer.
 */
export function chargerCasMetier(brut) {
    if (!estObjet(brut) || !Array.isArray(brut['cas'])) {
        throw new Error('Catalogue de cas métier illisible : clé « cas » absente ou non tableau.');
    }
    return brut['cas'].map((entree, index) => {
        if (!estObjet(entree) ||
            typeof entree['cle'] !== 'string' ||
            typeof entree['libelle'] !== 'string' ||
            typeof entree['reference'] !== 'string' ||
            typeof entree['explication'] !== 'string' ||
            !estObjet(entree['surcharges'])) {
            throw new Error(`Cas métier nº${String(index + 1)} incomplet.`);
        }
        return {
            cle: entree['cle'],
            libelle: entree['libelle'],
            reference: entree['reference'],
            explication: entree['explication'],
            surcharges: entree['surcharges'],
        };
    });
}
//# sourceMappingURL=scenario-presets.js.map